import * as THREE from 'three'
import type { AssetId, AssetKind, Disposable } from '@/shared/types/core'

/**
 * 场景包围盒注册条目，由资产插件提供。
 */
export interface SceneBoundsEntry {
  /** 资产 ID */
  readonly id: AssetId
  /** 资产种类 */
  readonly kind: AssetKind
  /** 资产在场景图中的 Object3D，用于取世界变换 */
  readonly object3D: THREE.Object3D
  /**
   * 获取资产局部坐标系下的包围盒。
   *
   * @returns 局部包围盒，尚未就绪返回 null
   */
  getLocalBoundingBox(): THREE.Box3 | null
  /**
   * 专用于 fit 拟合的世界包围盒，优先于 local 包围盒。
   *
   * @returns fit 用世界包围盒，无该接口或尚未就绪返回 null
   */
  getWorldBoundingBoxForFit?(): THREE.Box3 | null
  /**
   * 当前资产是否可见。
   *
   * @returns 可见返回 true，否则 false
   */
  isVisible(): boolean
}

/** 场景包围盒查询用途：默认或 fit 相机拟合 */
export type SceneBoundsPurpose = 'default' | 'fit'

/**
 * 场景包围盒查询参数。
 */
export interface SceneBoundsQuery {
  /** 只参与计算的资产 ID 集合，未指定时涵盖全部 */
  ids?: readonly AssetId[]
  /** 只参与计算的资产类型集合，未指定时涵盖全部 */
  kinds?: readonly AssetKind[]
  /** 是否包含不可见资产，默认 false */
  includeInvisible?: boolean
  /** 查询用途，决定是否优先使用 fit 专用包围盒 */
  purpose?: SceneBoundsPurpose
  /** 只聚合与该世界空间视锥相交的资产包围盒，未指定时不进行空间过滤 */
  frustum?: THREE.Frustum
}

/**
 * 统一的场景包围盒查询服务。
 *
 * 只接收资产级通用 entry，不感知任何插件内部实现类。
 */
export class SceneBoundsService implements Disposable {
  private entries = new Map<string, SceneBoundsEntry>()

  /**
   * 注册包围盒条目，重复 id+kind 会覆盖旧条目。
   *
   * @param entry - 条目实现，由资产插件提供
   */
  register(entry: SceneBoundsEntry): void {
    this.entries.set(this._makeKey(entry.kind, entry.id), entry)
  }

  /**
   * 注销指定条目。
   *
   * @param kind - 资产种类
   * @param id - 资产 ID
   */
  unregister(kind: AssetKind, id: AssetId): void {
    this.entries.delete(this._makeKey(kind, id))
  }

  /**
   * 聚合指定条件下的世界包围盒。
   *
   * @param query - 查询过滤条件
   * @returns 聚合后的世界包围盒，无匹配时返回空 Box3
   */
  getWorldBoundingBox(query: SceneBoundsQuery = {}): THREE.Box3 {
    const result = new THREE.Box3()
    const ids = query.ids ? new Set(query.ids) : null
    const kinds = query.kinds ? new Set(query.kinds) : null
    const includeInvisible = query.includeInvisible ?? false
    const purpose = query.purpose ?? 'default'

    for (const entry of this.entries.values()) {
      if (ids && !ids.has(entry.id)) continue
      if (kinds && !kinds.has(entry.kind)) continue
      if (!includeInvisible && !entry.isVisible()) continue

      if (purpose === 'fit' && entry.getWorldBoundingBoxForFit) {
        const fitBounds = entry.getWorldBoundingBoxForFit()?.clone() ?? null
        if (fitBounds && !fitBounds.isEmpty()) {
          if (!query.frustum || query.frustum.intersectsBox(fitBounds)) {
            result.union(fitBounds)
          }
          continue
        }
      }

      const localBounds = entry.getLocalBoundingBox()
      if (!localBounds) continue

      const localBox = localBounds.clone()
      if (localBox.isEmpty()) continue

      entry.object3D.updateWorldMatrix(true, false)
      const worldBox = localBox.applyMatrix4(entry.object3D.matrixWorld)
      if (!worldBox.isEmpty() && (!query.frustum || query.frustum.intersectsBox(worldBox))) {
        result.union(worldBox)
      }
    }

    return result
  }

  /** 清空所有已注册条目 */
  dispose(): void {
    this.entries.clear()
  }

  private _makeKey(kind: AssetKind, id: AssetId): string {
    return `${kind}:${id}`
  }
}
